import { useGetAddonsDataQuery, useUpdateAddonsDataMutation } from '@/src/api/rtkQuery/addonsApi'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import { AddonsType } from '@/src/types/addons'
import { getAncillaryUpdatePayload } from '@/src/utils/itinerary/updatePayloadHelper'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

const useAddonsCommon = () => {
    const itineraryId = useGetItineraryId()
    const { showError } = useThemedToast()
    const { data: addonsData, isLoading, isFetching, isError, isUninitialized } = useGetAddonsDataQuery({ itineraryId })
    const [updateAddonsData] = useUpdateAddonsDataMutation()

    const { ancillaryStatus, selectedAncillaries = [], journeyMap = [] } = addonsData || {}
    const t = useTranslations('itineraryPage.step')
    const availableAddons: AddonsType[] = useMemo(() => {
        const { isBaggageAvailable, isMealAvailable, isSeatAvailable } = ancillaryStatus || {}
        const availableAddons: AddonsType[] = []
        if (isSeatAvailable) availableAddons.push('SEAT')
        if (isMealAvailable) availableAddons.push('MEAL')
        if (isBaggageAvailable) availableAddons.push('BAGGAGE')

        return availableAddons
    }, [ancillaryStatus])

    const updateAddonsHandler = useCallback(
        async ({
            isOptimisticSuccessUpdate,
            onSuccess,
            onFailure
        }: {
            isOptimisticSuccessUpdate: boolean
            onSuccess: () => void
            onFailure: () => void
        }) => {
            const payload = getAncillaryUpdatePayload({
                journeyMap,
                selectedAncillaries
            })

            if (isOptimisticSuccessUpdate) {
                onSuccess?.()
            }

            const response = await updateAddonsData({ itineraryId, payload })
            if (response.error || !response.data.status) {
                // API failed, rollback to previous step
                onFailure?.()
                showError('Failed to update addons', {
                    id: 'update-addons-error'
                })
                return
            } else if (!isOptimisticSuccessUpdate) {
                onSuccess?.()
            }
        },
        [journeyMap, selectedAncillaries]
    )

    return {
        isLoading: isLoading || isFetching || isUninitialized,
        isError,
        itineraryId,
        stepTitle: t('addons'),
        availableAddons,
        updateAddonsHandler
    }
}

export default useAddonsCommon
